import { useEffect, useMemo, useRef, useState } from "react";
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Menu,
  Modal,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconCamera,
  IconCameraOff,
  IconDownload,
  IconEdit,
  IconFileSpreadsheet,
  IconFileTypePdf,
  IconPaperclip,
  IconPlus,
  IconPrinter,
  IconScan,
  IconTable,
  IconTool,
  IconTrash,
  IconTruckDelivery,
  IconX,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import type { Customer, ForeignModule, ReturnBox, ReturnBoxAttachment, ReturnBoxDetail, RmaListEntry } from "../../api/types";
import { BarcodeScanner } from "../../components/BarcodeScanner";
import { useLocalAgent } from "../../hooks/useLocalAgent";
import { localAgentPrint } from "../../api/localAgent";
import { DocumentCameraModal } from "../DocumentCameraModal";
import { ExportToSheetModal } from "../shipments/ExportToSheetModal";
import { ResponsiveTable } from "../responsiveTable/ResponsiveTable";

const DEDUPE_MS = 2000;

function statusColor(status: string): string {
  switch (status) {
    case "returned": return "orange";
    case "restored": return "teal";
    case "defect": return "red";
    default: return "gray";
  }
}

export function ReturnBoxesTab() {
  const { t } = useTranslation();
  const [boxes, setBoxes] = useState<ReturnBox[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ReturnBoxDetail | null>(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [scanMode, setScanMode] = useState<"add" | "restore" | "defect">("add");
  const [manual, setManual] = useState("");
  const [scanCandidates, setScanCandidates] = useState<RmaListEntry[]>([]);
  const [sheetExport, setSheetExport] = useState<"all" | "repaired" | "defect" | null>(null);
  const [foreignMode, setForeignMode] = useState(false);
  const [foreignModules, setForeignModules] = useState<ForeignModule[]>([]);

  const [sessionCount, setSessionCount] = useState(0);
  // Raw values seen this session — kept in state so the camera can draw them
  // yellow (knownRaws), plus a ref mirror so the scan handler reads fresh.
  const [seenKeys, setSeenKeys] = useState<Set<string>>(new Set());
  const seenKeysRef = useRef(seenKeys);
  useEffect(() => { seenKeysRef.current = seenKeys; }, [seenKeys]);
  function markSeen(...keys: (string | null | undefined)[]) {
    const add = keys.filter((k): k is string => !!k).map((k) => k.trim().toUpperCase());
    if (add.length === 0) return;
    for (const k of add) seenKeysRef.current.add(k);
    setSeenKeys((prev) => {
      const next = new Set(prev);
      for (const k of add) next.add(k);
      return next;
    });
  }
  const lastScanRef = useRef<{ raw: string; ts: number } | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    customer_id: null as string | null,
    received_at: new Date().toISOString().slice(0, 10),
    notes: "",
  });

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    act_number: "",
    predator_note: "",
    received_at: "",
    notes: "",
  });

  const [attachments, setAttachments] = useState<ReturnBoxAttachment[]>([]);
  const [docNote, setDocNote] = useState("");
  const [docCameraOpen, setDocCameraOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const localAgent = useLocalAgent();
  const [boxScan, setBoxScan] = useState("");
  const [boxCameraOn, setBoxCameraOn] = useState(false);
  const lastBoxScanRef = useRef<{ code: string; ts: number } | null>(null);

  async function loadBoxes() {
    try {
      const [b, c] = await Promise.all([api.listReturnBoxes(), api.listCustomers()]);
      setBoxes(b);
      setCustomers(c);
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function loadDetail(id: number) {
    try {
      const res = await api.getReturnBox(id);
      setDetail(res);
      setForeignModules(res.foreign_modules ?? []);
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function loadAttachments(id: number) {
    try {
      setAttachments(await api.listReturnBoxAttachments(id));
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function uploadDoc(file: File) {
    if (!activeId) return;
    try {
      await api.uploadReturnBoxAttachment(Number(activeId), file, docNote.trim() || undefined);
      setDocNote("");
      await loadAttachments(Number(activeId));
    } catch (e) {
      const tooLarge = e instanceof ApiError && (e.detail as { error?: string } | null)?.error === "file_too_large";
      notifications.show({
        color: "red",
        message: tooLarge ? t("rma.boxes.docs.tooLarge") : e instanceof ApiError ? e.message : String(e),
      });
    }
  }

  async function deleteDoc(attachmentId: number) {
    if (!activeId) return;
    if (!confirm(t("rma.boxes.docs.confirmDelete"))) return;
    try {
      await api.deleteReturnBoxAttachment(Number(activeId), attachmentId);
      await loadAttachments(Number(activeId));
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  useEffect(() => { void loadBoxes(); }, []);
  useEffect(() => {
    if (activeId) {
      void loadDetail(Number(activeId));
      void loadAttachments(Number(activeId));
      setSessionCount(0);
      seenKeysRef.current = new Set();
      setSeenKeys(new Set());
    } else {
      setDetail(null);
      setAttachments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const customerOptions = customers.map((c) => ({ value: String(c.id), label: c.name }));
  const boxOptions = boxes.map((b) => ({
    value: String(b.id),
    label: `${b.code}${b.customer_name ? " — " + b.customer_name : ""} (${b.total ?? 0})`,
  }));

  function resetSession() {
    setSessionCount(0);
    seenKeysRef.current = new Set();
    setSeenKeys(new Set());
  }

  async function scanExact(value: string) {
    const key = value.trim().toUpperCase();
    const wasSeen = seenKeysRef.current.has(key);
    const res = await api.scanIntoReturnBox(Number(activeId), value);
    markSeen(key, res.device?.dev_eui);
    if (!wasSeen && !res.already_here) {
      setSessionCount((n) => n + 1);
    }
    if (res.already_here) {
      notifications.show({ color: "yellow", message: t("rma.boxes.alreadyInBox") });
    }
    if (res.moved_from_box_id) {
      notifications.show({ color: "yellow", message: t("rma.boxes.movedFrom", { box: res.moved_from_box_id }) });
    }
    setManual("");
    await loadDetail(Number(activeId));
    await loadBoxes();
  }

  async function triageScanByEui(value: string) {
    await api.scanTriageReturnBox(Number(activeId), value, scanMode as "restore" | "defect");
    setManual("");
    await loadDetail(Number(activeId));
    await loadBoxes();
    notifications.show({
      color: scanMode === "restore" ? "teal" : "red",
      message: scanMode === "restore" ? t("rma.boxes.scanRestored") : t("rma.boxes.scanDefected"),
    });
  }

  async function triageScan(value: string) {
    if (!activeId) return;
    try {
      await triageScanByEui(value);
    } catch (e) {
      const code = e instanceof ApiError ? (e.detail as { error?: string } | null)?.error : undefined;
      if (code === "device_not_found" && value.length >= 2) {
        // Exact resolve failed — fall back to partial search, but only over
        // devices currently in THIS box (triage needs them present).
        try {
          const res = await api.searchDevices(value);
          const inBox = res.devices.filter((d) => d.return_box_id === Number(activeId));
          if (inBox.length === 1) {
            await triageScanByEui(inBox[0].dev_eui);
            return;
          }
          if (inBox.length > 1) {
            setScanCandidates(inBox);
            return;
          }
        } catch {
          // fall through to the not-found notification
        }
      }
      const msg =
        code === "not_in_box"
          ? t("rma.boxes.scanNotInBox", { q: value })
          : code === "device_not_found"
            ? t("rma.boxes.notFound", { q: value })
            : e instanceof ApiError
              ? e.message
              : String(e);
      notifications.show({ color: "red", message: msg });
    }
  }

  async function handleScan(raw: string) {
    const value = raw.trim();
    if (!value || !activeId) return;
    const now = Date.now();
    if (lastScanRef.current && lastScanRef.current.raw === value && now - lastScanRef.current.ts < DEDUPE_MS) {
      return;
    }
    lastScanRef.current = { raw: value, ts: now };

    if (foreignMode) {
      try {
        const r = await api.addForeignModule(Number(activeId), value);
        await loadDetail(Number(activeId));
        notifications.show({
          color: r.created ? "teal" : "yellow",
          message: r.created ? t("rma.boxes.foreignAdded", { code: r.module.code }) : t("rma.boxes.foreignDup", { code: r.module.code }),
        });
      } catch (e) {
        notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
      }
      return;
    }

    setScanCandidates([]);
    if (scanMode !== "add") {
      await triageScan(value);
      return;
    }
    try {
      await scanExact(value);
    } catch (e) {
      const notFound = e instanceof ApiError && (e.detail as { error?: string } | null)?.error === "device_not_found";
      if (notFound && value.length >= 2) {
        // Exact resolve failed — fall back to partial search, same as the Search tab.
        // One hit adds directly; several show a pick list.
        try {
          const res = await api.searchDevices(value);
          if (res.devices.length === 1) {
            await scanExact(res.devices[0].dev_eui);
            return;
          }
          if (res.devices.length > 1) {
            setScanCandidates(res.devices);
            return;
          }
        } catch {
          // fall through to the not-found notification
        }
      }
      const msg = notFound
        ? t("rma.boxes.notFound", { q: value })
        : (e instanceof ApiError ? e.message : String(e));
      notifications.show({ color: "red", message: msg });
    }
  }

  async function addCandidate(devEui: string) {
    // Picking a candidate applies the current scan mode: add to box, or triage
    // (mark repaired / defect) the device.
    setScanCandidates([]);
    try {
      if (scanMode === "add") {
        await scanExact(devEui);
      } else {
        await triageScanByEui(devEui);
      }
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function submitCreate() {
    try {
      const box = await api.createReturnBox({
        customer_id: form.customer_id ? Number(form.customer_id) : null,
        received_at: form.received_at || null,
        notes: form.notes.trim() || null,
      });
      setCreateOpen(false);
      setForm({ ...form, notes: "" });
      await loadBoxes();
      setActiveId(String(box.id));
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  function openEdit() {
    if (!detail) return;
    setEditForm({
      act_number: detail.box.act_number ?? "",
      predator_note: detail.box.predator_note ?? "",
      received_at: detail.box.received_at ?? "",
      notes: detail.box.notes ?? "",
    });
    setEditOpen(true);
  }

  async function submitEdit() {
    if (!activeId) return;
    try {
      await api.updateReturnBox(Number(activeId), {
        act_number: editForm.act_number.trim() || null,
        predator_note: editForm.predator_note.trim() || null,
        received_at: editForm.received_at || null,
        notes: editForm.notes.trim() || null,
      });
      setEditOpen(false);
      await loadDetail(Number(activeId));
      await loadBoxes();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  function downloadAct() {
    if (!activeId) return;
    window.location.href = api.returnBoxActPdfUrl(Number(activeId));
  }

  async function printReturnLabel() {
    if (!activeId) return;
    try {
      if (localAgent.godex) {
        const r = await api.godexPrintReturnBoxPayload(Number(activeId), "small");
        await localAgentPrint(r.ezpl_b64, localAgent.selectedId);
      } else {
        await api.godexPrintReturnBox(Number(activeId), "small");
      }
      notifications.show({ color: "green", message: t("rma.boxes.labelSent") });
    } catch (e) {
      // Surface the real Godex error (e.g. printer unreachable) instead of "API error 502".
      const detail = e instanceof ApiError ? (e.detail as { detail?: string; error?: string } | null) : null;
      const msg = detail?.detail || detail?.error || (e instanceof ApiError ? e.message : String(e));
      notifications.show({ color: "red", message: t("rma.boxes.printFailed", { reason: msg }) });
    }
  }

  async function openBoxByCode(code: string) {
    try {
      const b = await api.returnBoxByCode(code);
      setActiveId(String(b.id));
      setBoxScan("");
      setBoxCameraOn(false);
      notifications.show({ color: "green", message: t("rma.boxes.scanBoxOpened", { code: b.code }) });
    } catch (e) {
      const code404 = e instanceof ApiError && e.status === 404;
      notifications.show({
        color: "red",
        message: code404 ? t("rma.boxes.scanBoxNotFound", { code }) : e instanceof ApiError ? e.message : String(e),
      });
    }
  }

  async function findBoxByCode() {
    const code = boxScan.trim();
    if (!code) return;
    await openBoxByCode(code);
  }

  // Camera scan of a return-box QR (RET-…) → open the box. The QR may hold the
  // bare code or a URL containing it, so pull the RET-code out of the raw text.
  function handleBoxCameraScan(raw: string) {
    const text = raw.trim();
    const m = text.toUpperCase().match(/RET-\d{4}-\d+/);
    const code = m ? m[0] : text;
    if (!code) return;
    const now = Date.now();
    if (lastBoxScanRef.current && lastBoxScanRef.current.code === code && now - lastBoxScanRef.current.ts < DEDUPE_MS) {
      return;
    }
    lastBoxScanRef.current = { code, ts: now };
    void openBoxByCode(code);
  }

  function exportBox(
    format: "xlsx" | "pdf",
    filter: "all" | "repaired" | "defect",
    xlsxFormat?: "full",
  ) {
    if (!activeId) return;
    window.location.href = api.returnBoxExportUrl(Number(activeId), format, filter, xlsxFormat);
  }

  async function transferToShipment() {
    if (!activeId) return;
    if (!confirm(t("rma.boxes.confirmTransfer"))) return;
    try {
      const r = await api.transferReturnBoxToShipment(Number(activeId));
      notifications.show({
        color: "teal",
        message: t("rma.boxes.transferredMsg", { count: r.transferred, code: r.shipment.code }),
      });
      await loadDetail(Number(activeId));
      await loadBoxes();
    } catch (e) {
      const msg = e instanceof ApiError && (e.detail as { error?: string } | null)?.error === "no_restored_devices"
        ? t("rma.boxes.transferNoRestored")
        : (e instanceof ApiError ? e.message : String(e));
      notifications.show({ color: "red", message: msg });
    }
  }

  async function markRestore(deviceId: number) {
    if (!activeId) return;
    try {
      await api.restoreInReturnBox(Number(activeId), deviceId);
      await loadDetail(Number(activeId));
      await loadBoxes();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function markDefect(deviceId: number) {
    if (!activeId) return;
    if (!confirm(t("rma.boxes.confirmDefect"))) return;
    try {
      await api.defectInReturnBox(Number(activeId), deviceId);
      await loadDetail(Number(activeId));
      await loadBoxes();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function markAllRestored() {
    if (!activeId) return;
    if (!confirm(t("rma.boxes.confirmRestoreAll"))) return;
    try {
      const res = await api.restoreAllInReturnBox(Number(activeId));
      notifications.show({ color: "teal", message: t("rma.boxes.restoredAll", { count: res.restored }) });
      await loadDetail(Number(activeId));
      await loadBoxes();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function markAllDefect() {
    if (!activeId) return;
    if (!confirm(t("rma.boxes.confirmDefectAll"))) return;
    try {
      const res = await api.defectAllInReturnBox(Number(activeId));
      notifications.show({ color: "red", message: t("rma.boxes.defectedAll", { count: res.defect }) });
      await loadDetail(Number(activeId));
      await loadBoxes();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function removeDevice(deviceId: number) {
    if (!activeId) return;
    try {
      await api.removeFromReturnBox(Number(activeId), deviceId);
      await loadDetail(Number(activeId));
      await loadBoxes();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  const counts = useMemo(() => detail?.box ?? null, [detail]);

  return (
    <Stack>
      <Group justify="space-between" wrap="wrap">
        <Group gap="sm" align="end">
          <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateOpen(true)}>
            {t("rma.boxes.add")}
          </Button>
          <Select
            label={t("rma.boxes.active")}
            placeholder={t("rma.boxes.activePlaceholder")}
            data={boxOptions}
            value={activeId}
            onChange={setActiveId}
            searchable
            clearable
            w={320}
            maw="100%"
          />
          <TextInput
            label={t("rma.boxes.scanBoxLabel")}
            placeholder="RET-2026-001"
            value={boxScan}
            onChange={(e) => setBoxScan(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void findBoxByCode(); }}
            leftSection={<IconScan size={16} />}
            w={200}
          />
          <Switch
            label={boxCameraOn ? t("rma.boxes.scanBoxCameraOn") : t("rma.boxes.scanBoxCamera")}
            checked={boxCameraOn}
            onChange={(e) => setBoxCameraOn(e.currentTarget.checked)}
            thumbIcon={boxCameraOn ? <IconCamera size={12} /> : <IconCameraOff size={12} />}
          />
        </Group>
        {activeId && detail && (
          <Group gap="xs">
            <Button
              variant="default"
              leftSection={<IconEdit size={14} />}
              onClick={openEdit}
              size="sm"
            >
              {t("rma.boxes.edit")}
            </Button>
            <Button
              variant="default"
              leftSection={<IconPrinter size={14} />}
              onClick={() => void printReturnLabel()}
              size="sm"
            >
              {t("rma.boxes.printLabel")}
            </Button>
            <Button
              variant="default"
              leftSection={<IconDownload size={14} />}
              onClick={downloadAct}
              disabled={(detail.box.total ?? 0) === 0}
              size="sm"
            >
              {t("rma.boxes.downloadAct")}
            </Button>
            <Menu shadow="md" position="bottom-start">
              <Menu.Target>
                <Button variant="default" leftSection={<IconDownload size={14} />} size="sm">
                  {t("rma.boxes.exportList")}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>XLSX</Menu.Label>
                <Menu.Item leftSection={<IconFileSpreadsheet size={14} />} onClick={() => exportBox("xlsx", "all")}>{t("rma.boxes.exportAll")}</Menu.Item>
                <Menu.Item leftSection={<IconFileSpreadsheet size={14} />} onClick={() => exportBox("xlsx", "repaired")}>{t("rma.boxes.exportRepaired")}</Menu.Item>
                <Menu.Item leftSection={<IconFileSpreadsheet size={14} />} onClick={() => exportBox("xlsx", "defect")}>{t("rma.boxes.exportDefect")}</Menu.Item>
                <Menu.Divider />
                <Menu.Label>{t("rma.boxes.exportFullLabel")}</Menu.Label>
                <Menu.Item leftSection={<IconFileSpreadsheet size={14} />} onClick={() => exportBox("xlsx", "all", "full")}>{t("rma.boxes.exportAll")}</Menu.Item>
                <Menu.Item leftSection={<IconFileSpreadsheet size={14} />} onClick={() => exportBox("xlsx", "repaired", "full")}>{t("rma.boxes.exportRepaired")}</Menu.Item>
                <Menu.Item leftSection={<IconFileSpreadsheet size={14} />} onClick={() => exportBox("xlsx", "defect", "full")}>{t("rma.boxes.exportDefect")}</Menu.Item>
                <Menu.Divider />
                <Menu.Label>PDF</Menu.Label>
                <Menu.Item leftSection={<IconFileTypePdf size={14} />} onClick={() => exportBox("pdf", "all")}>{t("rma.boxes.exportAll")}</Menu.Item>
                <Menu.Item leftSection={<IconFileTypePdf size={14} />} onClick={() => exportBox("pdf", "repaired")}>{t("rma.boxes.exportRepaired")}</Menu.Item>
                <Menu.Item leftSection={<IconFileTypePdf size={14} />} onClick={() => exportBox("pdf", "defect")}>{t("rma.boxes.exportDefect")}</Menu.Item>
                <Menu.Divider />
                <Menu.Label>{t("rma.boxes.exportSheetLabel")}</Menu.Label>
                <Menu.Item leftSection={<IconTable size={14} />} onClick={() => setSheetExport("all")}>{t("rma.boxes.exportAll")}</Menu.Item>
                <Menu.Item leftSection={<IconTable size={14} />} onClick={() => setSheetExport("repaired")}>{t("rma.boxes.exportRepaired")}</Menu.Item>
                <Menu.Item leftSection={<IconTable size={14} />} onClick={() => setSheetExport("defect")}>{t("rma.boxes.exportDefect")}</Menu.Item>
              </Menu.Dropdown>
            </Menu>
            <Button
              color="blue"
              leftSection={<IconTruckDelivery size={14} />}
              onClick={() => void transferToShipment()}
              disabled={(detail.box.restored ?? 0) === 0}
              size="sm"
            >
              {t("rma.boxes.transfer", { count: detail.box.restored ?? 0 })}
            </Button>
            {detail.box.transferred_shipment_id && (
              <Badge color="blue" variant="light">
                {t("rma.boxes.transferredBadge", { id: detail.box.transferred_shipment_id })}
              </Badge>
            )}
          </Group>
        )}
      </Group>

      {boxCameraOn && (
        <Card withBorder>
          <Title order={6} mb="xs">{t("rma.boxes.scanBoxCameraTitle")}</Title>
          <div style={{ maxWidth: 480 }}>
            <BarcodeScanner active onDetect={handleBoxCameraScan} />
          </div>
        </Card>
      )}

      {activeId && (
        <Card withBorder>
          <Title order={5} mb="xs">{t("rma.boxes.scanInto", { code: detail?.box.code ?? "" })}</Title>
          <Group gap="sm" wrap="wrap" align="end">
            <TextInput
              placeholder={t("rma.boxes.scanPlaceholder")}
              value={manual}
              onChange={(e) => setManual(e.currentTarget.value)}
              onKeyDown={(e) => { if (e.key === "Enter") void handleScan(manual); }}
              autoFocus
              style={{ flex: "1 1 260px" }}
            />
            <Switch
              label={cameraOn ? t("scanner.cameraOn") : t("scanner.cameraOff")}
              checked={cameraOn}
              onChange={(e) => setCameraOn(e.currentTarget.checked)}
              thumbIcon={cameraOn ? <IconCamera size={12} /> : <IconCameraOff size={12} />}
            />
          </Group>
          <SegmentedControl
            mt="sm"
            size="xs"
            value={scanMode}
            onChange={(v) => setScanMode(v as "add" | "restore" | "defect")}
            data={[
              { label: t("rma.boxes.scanModeAdd"), value: "add" },
              { label: t("rma.boxes.scanModeRestore"), value: "restore" },
              { label: t("rma.boxes.scanModeDefect"), value: "defect" },
            ]}
          />
          <Switch
            mt="sm"
            label={t("rma.boxes.foreignMode")}
            checked={foreignMode}
            onChange={(e) => setForeignMode(e.currentTarget.checked)}
          />
          {cameraOn && (
            <div style={{ position: "relative", maxWidth: 480, marginTop: 12 }}>
              <BarcodeScanner active onDetect={(raw) => void handleScan(raw)} knownRaws={seenKeys} />
              <div
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  background: "rgba(0,0,0,0.65)",
                  color: "white",
                  padding: "4px 10px",
                  borderRadius: 8,
                  fontVariantNumeric: "tabular-nums",
                  display: "flex",
                  alignItems: "baseline",
                  gap: 6,
                }}
              >
                <Text component="span" size="xl" fw={700} c="white" lh={1}>{sessionCount}</Text>
                <Text component="span" size="xs" c="white" opacity={0.85}>{t("scanner.newCountSuffix")}</Text>
                <Text component="span" size="xs" c="white" opacity={0.6}>/</Text>
                <Text component="span" size="md" fw={600} c="white" lh={1}>{counts?.total ?? 0}</Text>
                <Text component="span" size="xs" c="white" opacity={0.85}>{t("rma.boxes.inBoxSuffix")}</Text>
              </div>
            </div>
          )}
          {scanCandidates.length > 0 && (
            <>
              <Title order={6} mt="sm" mb="xs">
                {t("rma.candidatesTitle", { count: scanCandidates.length })}
              </Title>
              <ResponsiveTable
                stagger={false}
                mobile={{
                  rows: scanCandidates,
                  rowKey: (d) => d.id,
                  title: (d) => <Text span ff="monospace" size="sm" fw={600}>{d.dev_eui.toLowerCase()}</Text>,
                  subtitle: (d) => [d.model, d.serial_number].filter(Boolean).join(" · ") || undefined,
                  badge: (d) => (
                    <Badge color={statusColor(d.status)} variant="light">
                      {d.status}
                    </Badge>
                  ),
                  fields: [
                    { label: t("rma.colCustomer"), render: (d) => d.customer_name ?? "—" },
                  ],
                  actions: (d) => (
                    <Button size="sm" variant="light" leftSection={<IconPlus size={16} />} onClick={() => void addCandidate(d.dev_eui)}>
                      {t("common.add")}
                    </Button>
                  ),
                }}
              >
              <Table.ScrollContainer minWidth={600}>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>DEV_EUI</Table.Th>
                      <Table.Th>{t("rma.colModel")}</Table.Th>
                      <Table.Th>{t("rma.colSerial")}</Table.Th>
                      <Table.Th>{t("rma.colStatus")}</Table.Th>
                      <Table.Th>{t("rma.colCustomer")}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {scanCandidates.map((d) => (
                      <Table.Tr
                        key={d.id}
                        style={{ cursor: "pointer" }}
                        onClick={() => void addCandidate(d.dev_eui)}
                      >
                        <Table.Td ff="monospace">{d.dev_eui.toLowerCase()}</Table.Td>
                        <Table.Td>{d.model ?? "—"}</Table.Td>
                        <Table.Td>{d.serial_number ?? "—"}</Table.Td>
                        <Table.Td>
                          <Badge color={statusColor(d.status)} variant="light">
                            {d.status}
                          </Badge>
                        </Table.Td>
                        <Table.Td>{d.customer_name ?? "—"}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
              </ResponsiveTable>
            </>
          )}
          <Group gap="md" mt="sm">
            <Badge size="lg" variant="filled">
              {t("rma.boxes.sessionCount", { count: sessionCount })}
            </Badge>
            <Button size="xs" variant="subtle" leftSection={<IconX size={14} />} onClick={resetSession}>
              {t("rma.boxes.reset")}
            </Button>
            <Text size="sm" c="dimmed">
              {t("rma.boxes.boxTotal", { count: counts?.total ?? 0 })}
            </Text>
          </Group>
        </Card>
      )}

      {detail && (
        <Card withBorder>
          <Group justify="space-between" mb="xs" wrap="wrap">
            <Title order={5}>{t("rma.boxes.contentTitle")}</Title>
            <Group gap="xs">
              <Badge variant="light">{t("rma.boxes.statTotal", { count: counts?.total ?? 0 })}</Badge>
              <Badge color="teal" variant="light">{t("rma.boxes.statRestored", { count: counts?.restored ?? 0 })}</Badge>
              <Badge color="orange" variant="light">{t("rma.boxes.statPending", { count: counts?.pending ?? 0 })}</Badge>
            </Group>
          </Group>
          {detail.devices.length > 0 && (counts?.pending ?? 0) > 0 && (
            <Group gap="xs" mb="sm">
              <Button
                size="xs"
                color="teal"
                leftSection={<IconTool size={14} />}
                onClick={() => void markAllRestored()}
              >
                {t("rma.boxes.restoreAll", { count: counts?.pending ?? 0 })}
              </Button>
              <Button
                size="xs"
                color="red"
                variant="light"
                onClick={() => void markAllDefect()}
              >
                {t("rma.boxes.defectAll", { count: counts?.pending ?? 0 })}
              </Button>
            </Group>
          )}
          <ResponsiveTable
            stagger={false}
            mobile={{
              rows: detail.devices,
              rowKey: (d) => d.id,
              title: (d) => <Text span ff="monospace" size="sm" fw={600}>{d.dev_eui.toLowerCase()}</Text>,
              subtitle: (d) => d.model ?? undefined,
              badge: (d) => <Badge color={statusColor(d.status)} variant="light">{d.status}</Badge>,
              fields: [],
              actions: (d) => (
                <Group gap="xs" wrap="wrap">
                  {d.status !== "restored" && (
                    <Button size="sm" color="teal" variant="light" leftSection={<IconTool size={14} />} onClick={() => void markRestore(d.id)}>
                      {t("rma.boxes.repaired")}
                    </Button>
                  )}
                  <Button size="sm" color="red" variant="light" onClick={() => void markDefect(d.id)}>
                    {t("rma.boxes.defect")}
                  </Button>
                  <ActionIcon variant="subtle" color="gray" size="lg" onClick={() => void removeDevice(d.id)} title={t("rma.boxes.remove")}>
                    <IconTrash size={18} />
                  </ActionIcon>
                </Group>
              ),
              empty: t("rma.boxes.empty"),
            }}
          >
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>DEV_EUI</Table.Th>
                <Table.Th>{t("rma.boxes.colModel")}</Table.Th>
                <Table.Th>{t("rma.boxes.colStatus")}</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {detail.devices.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={4}><Text c="dimmed" size="sm">{t("rma.boxes.empty")}</Text></Table.Td>
                </Table.Tr>
              ) : (
                detail.devices.map((d) => (
                  <Table.Tr key={d.id}>
                    <Table.Td><Text ff="monospace" size="sm">{d.dev_eui.toLowerCase()}</Text></Table.Td>
                    <Table.Td>{d.model ?? "—"}</Table.Td>
                    <Table.Td>
                      <Badge color={statusColor(d.status)} variant="light">{d.status}</Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" justify="flex-end" wrap="nowrap">
                        {d.status !== "restored" && (
                          <Button size="xs" color="teal" variant="light" leftSection={<IconTool size={12} />} onClick={() => void markRestore(d.id)}>
                            {t("rma.boxes.repaired")}
                          </Button>
                        )}
                        <Button size="xs" color="red" variant="light" onClick={() => void markDefect(d.id)}>
                          {t("rma.boxes.defect")}
                        </Button>
                        <ActionIcon variant="subtle" color="gray" onClick={() => void removeDevice(d.id)} title={t("rma.boxes.remove")}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
          </ResponsiveTable>
          {(foreignMode || foreignModules.length > 0) && (
            <Paper withBorder p="sm" mt="md">
              <Text fw={600} size="sm" mb="xs">{t("rma.boxes.foreignList")}</Text>
              {foreignModules.length === 0 ? (
                <Text c="dimmed" size="sm">{t("rma.boxes.foreignEmpty")}</Text>
              ) : (
                <Stack gap={4}>
                  {foreignModules.map((m) => (
                    <Group key={m.id} justify="space-between" wrap="nowrap">
                      <Text ff="monospace" size="sm">{m.code}</Text>
                      <ActionIcon color="red" variant="subtle" title={t("rma.boxes.foreignDelete")}
                        onClick={async () => {
                          try {
                            await api.deleteForeignModule(Number(activeId), m.id);
                            await loadDetail(Number(activeId));
                          } catch (e) {
                            notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
                          }
                        }}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  ))}
                </Stack>
              )}
            </Paper>
          )}
        </Card>
      )}

      {detail && (
        <Card withBorder>
          <Title order={5} mb="xs">{t("rma.boxes.docs.title")}</Title>
          <Group gap="sm" align="end" wrap="wrap" mb="sm">
            <TextInput
              label={t("rma.boxes.docs.noteLabel")}
              placeholder={t("rma.boxes.docs.notePlaceholder")}
              value={docNote}
              onChange={(e) => setDocNote(e.currentTarget.value)}
              style={{ flex: "1 1 240px" }}
            />
            <Button variant="default" leftSection={<IconPaperclip size={16} />} onClick={() => fileInputRef.current?.click()}>
              {t("rma.boxes.docs.file")}
            </Button>
            <Button variant="default" leftSection={<IconCamera size={16} />} onClick={() => setDocCameraOpen(true)}>
              {t("rma.boxes.docs.camera")}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              onChange={(e) => {
                const f = e.currentTarget.files?.[0];
                if (f) void uploadDoc(f);
                e.currentTarget.value = "";
              }}
            />
          </Group>
          {attachments.length === 0 ? (
            <Text c="dimmed" size="sm">{t("rma.boxes.docs.empty")}</Text>
          ) : (
            <Stack gap={4}>
              {attachments.map((a) => (
                <Group key={a.id} justify="space-between" wrap="nowrap">
                  <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                    <Anchor
                      href={api.returnBoxAttachmentDownloadUrl(Number(activeId), a.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="sm"
                      truncate
                    >
                      {a.original_filename}
                    </Anchor>
                    {a.note && <Text size="xs" c="dimmed" truncate>— {a.note}</Text>}
                  </Group>
                  <ActionIcon variant="subtle" color="red" onClick={() => void deleteDoc(a.id)} title={t("rma.boxes.docs.delete")}>
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
          )}
          <DocumentCameraModal
            opened={docCameraOpen}
            onClose={() => setDocCameraOpen(false)}
            onCapture={(f) => void uploadDoc(f)}
          />
        </Card>
      )}

      <ExportToSheetModal
        opened={sheetExport !== null}
        onClose={() => setSheetExport(null)}
        target="box"
        boxId={activeId ? Number(activeId) : undefined}
        filter={sheetExport ?? "all"}
      />

      <Modal opened={createOpen} onClose={() => setCreateOpen(false)} title={t("rma.boxes.add")} centered>
        <Stack>
          <Select
            label={t("rma.boxes.customer")}
            data={customerOptions}
            value={form.customer_id}
            onChange={(v) => setForm({ ...form, customer_id: v })}
            searchable
            clearable
          />
          <TextInput
            type="date"
            label={t("rma.boxes.receivedAt")}
            value={form.received_at}
            onChange={(e) => setForm({ ...form, received_at: e.currentTarget.value })}
          />
          <Textarea
            label={t("rma.boxes.notes")}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.currentTarget.value })}
            minRows={2}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setCreateOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={() => void submitCreate()}>{t("common.save")}</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={editOpen} onClose={() => setEditOpen(false)} title={t("rma.boxes.edit")} centered>
        <Stack>
          <TextInput
            label={t("rma.boxes.actNumber")}
            description={t("rma.boxes.actNumberHint")}
            placeholder="PE-MMYY/NNN"
            value={editForm.act_number}
            onChange={(e) => setEditForm({ ...editForm, act_number: e.currentTarget.value })}
          />
          <Textarea
            label={t("rma.boxes.predatorNote")}
            description={t("rma.boxes.predatorNoteHint")}
            value={editForm.predator_note}
            onChange={(e) => setEditForm({ ...editForm, predator_note: e.currentTarget.value })}
            minRows={2}
          />
          <TextInput
            type="date"
            label={t("rma.boxes.receivedAt")}
            value={editForm.received_at}
            onChange={(e) => setEditForm({ ...editForm, received_at: e.currentTarget.value })}
          />
          <Textarea
            label={t("rma.boxes.notes")}
            value={editForm.notes}
            onChange={(e) => setEditForm({ ...editForm, notes: e.currentTarget.value })}
            minRows={2}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setEditOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={() => void submitEdit()}>{t("common.save")}</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
